import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { setPreferences } from "@/lib/prefs";
import { detectCurrencyFromLocation } from "@/lib/locale";

const AuthContext = createContext(null);

/**
 * On first sign-in the user's profile has no locale settings. Detect them
 * from the browser (Intl API + timezone→country→currency table) and PATCH
 * the profile so future sessions inherit them. Runs at most once per user:
 * subsequent calls short-circuit because `prefs` is already populated.
 */
async function persistDetectedPreferencesIfMissing(userData) {
  const prefs = userData?.preferences || {};
  const missing = {};
  if (!prefs.currency) missing.currency = detectCurrencyFromLocation();
  if (!prefs.timezone) {
    try { missing.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone; }
    catch { /* stick with server default */ }
  }
  if (!prefs.locale && typeof navigator !== "undefined") {
    missing.locale = (navigator.language || "en-US").split("@")[0].replace(/_/g, "-");
  }
  if (Object.keys(missing).length === 0) return userData;
  try {
    const { data } = await api.patch("/auth/preferences", missing);
    return data;
  } catch {
    // Non-fatal. UI still uses in-memory detection until next login.
    return { ...userData, preferences: { ...prefs, ...missing } };
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = unknown/loading, false = logged out
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      const withPrefs = await persistDetectedPreferencesIfMissing(data);
      setUser(withPrefs);
      setPreferences(withPrefs?.preferences || {});
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from Google OAuth, skip /me: AuthCallback handles it.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      const withPrefs = await persistDetectedPreferencesIfMissing(data);
      setUser(withPrefs);
      setPreferences(withPrefs?.preferences || {});
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (email, password, name, extras = {}) => {
    try {
      const { data } = await api.post("/auth/register", { email, password, name, ...extras });
      const withPrefs = await persistDetectedPreferencesIfMissing(data);
      setUser(withPrefs);
      setPreferences(withPrefs?.preferences || {});
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      setUser(false);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
